/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { cn } from '@/lib/utils';
import type { RefinementProps } from '../types';

/**
 * `Filter Item` — a value row in the filter rail, per the SRAM **Filters** pattern
 * (Figma `6952:46600`, instantiated on the PLP frame `1396:10895`). CRCB2B-348.
 *
 * ## No control is drawn, but the semantics are a checkbox's
 *
 * The design draws no checkbox on a value row: selection is communicated by the row's own
 * fill. But a filter group is inherently multi-select and the AC requires selections to be
 * "programmatically identifiable", so the control is present and merely invisible — a real
 * `input[type=checkbox]`, `sr-only`, wrapped by the `<label>` that is the row.
 *
 * That wrapping is what makes the **whole row** activate the filter: it is native label
 * behaviour, not a click handler, so it works with pointer, keyboard and assistive tech
 * without this component tracking any state of its own. The selected treatment likewise comes
 * from `has-[:checked]:` reading the live control rather than from a second source of truth.
 *
 * This follows the row mechanism the `choice-list` spec documents for radios, but **not** its
 * `FieldLabel`/`Field` wrapper. That wrapper is tuned for Choice List's 56px bordered rows with
 * an `--accent` fill; reusing it here would mean overriding its border, its padding, its row
 * height and its checked fill — and neutralising its border would require `border-0`, which
 * the project's shape-token rules forbid outright. The mechanism is reused; the chrome is not.
 * (Deviation from design.md D4, which named the wrapper. Recorded there.)
 *
 * ## Measured treatment
 *
 * | Part | Design token | Value |
 * |---|---|---|
 * | row height | — | 48px |
 * | unselected fill | `fill-neutral-subtle` -> `--muted` | `#f5f5f5` |
 * | selected fill | `fill-neutral` -> `--primary` | `#141414` |
 * | selected label | `on-surface-contrast` -> `--primary-foreground` | white, bold |
 * | label | `text-color-primary` -> `--foreground` | `#000`, 16px |
 * | count | `on-fill-neutral-subtle` -> `--supporting-foreground` | `#4a4a4a` |
 * | disabled label + count | `text-color-tertiary` -> `--text-tertiary` | `#676767` |
 *
 * **The disabled row is `--text-tertiary` (`#676767`), not `--disabled-foreground`
 * (`#7d7d7d`).** The Filters pattern greys unavailable values with the tertiary text colour,
 * which is one ramp step darker than the disabled colour this repo uses elsewhere. Stated so
 * it is not "corrected" to the disabled token later — it is the design's value, deliberately.
 */
export default function DefaultRefinement({
    values,
    attributeId,
    isFilterSelected,
    toggleFilter,
    idPrefix,
    groupLabel,
}: RefinementProps): ReactElement {
    const scope = idPrefix ? `${idPrefix}-` : '';

    return (
        // A real group, so a value row announces which filter it belongs to. The legend is
        // visually hidden because the accordion trigger already shows the group name.
        // 8px row gap, measured from the PLP instance (`0:580`-`0:610`: 48px rows stepping by
        // 56). The pattern page's own 2-up `item grid` steps by 60 (12px); the PLP frame is the
        // binding instance for this ticket, so 8px wins.
        <fieldset className="flex flex-col gap-2">
            {groupLabel && <legend className="sr-only">{groupLabel}</legend>}
            {values.map((value, idx) => {
                const id = `${scope}refine-${attributeId}-${idx}`;
                const isSelected = isFilterSelected(attributeId, value.value);
                // A value the current result set cannot produce. The design keeps it in place,
                // greyed, with its zero still shown — it says "this exists, but not here",
                // which is the whole reason for greying rather than hiding.
                const isUnavailable = value.hitCount === 0;

                return (
                    <label
                        key={`${attributeId}:${value.value}`}
                        htmlFor={id}
                        className={cn(
                            'group/row flex h-12 items-center justify-between gap-4 px-4',
                            'text-base leading-5 tracking-[0.03em]',
                            'bg-muted text-foreground',
                            'transition-colors',
                            // Selected: inverted fill, contrast text, bold label.
                            'has-[:checked]:bg-primary has-[:checked]:font-bold has-[:checked]:text-primary-foreground',
                            // The focus ring belongs to the row, because the control that
                            // receives focus is invisible.
                            'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-ring has-[:focus-visible]:-outline-offset-2',
                            isUnavailable
                                ? 'cursor-not-allowed bg-background text-text-tertiary'
                                : 'cursor-pointer hover:bg-muted-hover'
                        )}>
                        <input
                            id={id}
                            type="checkbox"
                            className="sr-only"
                            checked={isSelected}
                            // `disabled` rather than `aria-disabled`: a row that cannot be
                            // acted on should not be a focus stop that does nothing. The
                            // trade-off is that a keyboard user meets it while reading the
                            // group rather than while tabbing it, which is the lesser cost.
                            // (design.md D5.)
                            disabled={isUnavailable}
                            onChange={() => toggleFilter(attributeId, value.value)}
                        />
                        <span className="min-w-0 truncate">{value.label || value.value}</span>
                        {value.hitCount !== undefined && (
                            <span
                                className={cn(
                                    'shrink-0 tabular-nums',
                                    isUnavailable ? 'text-text-tertiary' : 'text-supporting-foreground',
                                    // On the inverted fill the count must flip with the label.
                                    // Left at #4a4a4a it would sit on #141414 — 1.7:1, a
                                    // contrast failure. Keyed off the row's own checked state
                                    // via the `group/row` marker on the label.
                                    'group-has-[:checked]/row:text-primary-foreground'
                                )}>
                                {value.hitCount}
                            </span>
                        )}
                    </label>
                );
            })}
        </fieldset>
    );
}
